<script lang="ts">
	import { Button, Text, Input } from '@Shelly/shelly-design-system';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';

	import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { format } from 'svelte-i18n';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindSettingsDrilldownBack, bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		isBleSettingsSessionReady,
		persistBleDeviceSetting,
		readStoredBleDevice,
		resumeBleSettingsConnectionIfNeeded,
	} from 'src/helpers/ble_settings_session';
	import DynamicAlert from './DynamicAlert.svelte';

	export let device;

	const min = 0;
	const max = 1000000;
	const command = 'setFireGroup';
	const param = 'fireGroup';

	let fireGroup: any = readStoredBleDevice(device)?.fireGroup || min;
	let hadSession = false;

	$: sessionReady = isBleSettingsSessionReady($controlBleDevice.connectionState);
	$: if (sessionReady) hadSession = true;
	$: resumeBleSettingsConnectionIfNeeded(device.id, $controlBleDevice);
	$: controlsDisabled = !sessionReady || !$app;

	const goBackToSettingsList = bindSettingsDrilldownBack();

	const onSave = () => {
		if ($controlBleDevice.connectionState === 2) {
			if ($os === 'IOS') {
				control_bluetooth_general_ios(device.id, command, param, fireGroup);
			} else {
				control_bluetooth_general_android(device.id, command, param, fireGroup);
			}

			persistBleDeviceSetting(device, { fireGroup });
			goBackToSettingsList?.();
		} else {
			if ($os === 'IOS') {
				control_bluetooth_device_ios(device.id, 'connect');
			} else {
				control_bluetooth_device_android(device.id, 'connect');
			}
		}
	};

	$: error = valueOutOfRange(fireGroup, min, max)
		? $format('error_out_of_range', {
				default: `Value ${fireGroup} is out of range. Please provide a value within the limits. (${min} — ${max})!`,
				values: { value: fireGroup, max, min, unit: '°' },
			})
		: '';

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: onSave,
				loading: false,
				disabled: controlsDisabled || !!error,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<DynamicAlert showExpiredMsg={hadSession} />

<Text class="mb-s ml-s" element="p" styleType="body-small">
	{$format('text_blu_setting_fire_group', {
		default: 'Set a fire group.',
	})}
</Text>

<Input
	id="input-i9c9nc2"
	aria-label={$format('input_blu_setting_fire_group', {
		default: `Enter the fire group. Value must be between ${min} — ${max}!`,
		values: { min, max },
	})}
	icon="fas fa-calculator"
	class="mb-m"
	bind:value={fireGroup}
	type="number"
	step={1}
	{min}
	{max}
	disabled={controlsDisabled}
	info={$format('movement_limit_toast', {
		default: `Values must be between ${min} — ${max}!`,
		values: { min, max },
	})}
	{error}
/>

{#if !$showNewLayout}
	<Button disabled={controlsDisabled || !!error} size="m" width="full" on:click={onSave}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
